import VehicleLayoutCanvas from '@/components/purchase/checkout/seat-selection/modal/canvas/VehicleLayoutCanvas';
import { useSeatSelection } from '@/hooks/useSeatSelection';
import { useSeatSelectionLegs } from '@/hooks/useSeatSelectionLegs';
import { TransText } from '@/i18n/trans/text';
import {
  getSelectedSeatCoordinates,
  transformCoordinates,
} from '@/utils/seatSelection';
import { Icons, Typography } from '@packages/webshop-design-system';
import { useEffect, useRef, useState } from 'react';
import { useMediaQuery } from 'react-responsive';

const getScrollParent = (element: HTMLElement | null): HTMLElement | null => {
  let parent = element?.parentElement ?? null;

  while (parent) {
    const { overflowX, overflowY } = getComputedStyle(parent);

    if (
      overflowY === 'auto' ||
      overflowY === 'scroll' ||
      overflowX === 'auto' ||
      overflowX === 'scroll'
    ) {
      return parent;
    }

    parent = parent.parentElement;
  }

  return null;
};

export const VehicleLayoutContainer = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [canvasReady, setCanvasReady] = useState(false);
  const {
    currentPlaceMap,
    floorPlanIcons,
    selectedReservationId,
    selectedFloorIndex,
    selectedCarriageIndex,
    selectedLegId,
    handleSeatSelect,
    handlePassengerSeatClick,
  } = useSeatSelection();
  const { legs } = useSeatSelectionLegs();
  const currentFloor = currentPlaceMap?.floors?.[selectedFloorIndex];
  const selectedLeg = legs.find((leg) => leg.id === selectedLegId);
  const allPassengers = selectedLeg?.passengers.map(
    ({ firstName, lastName, reservationId }, key) => ({
      firstName,
      lastName,
      reservationId,
      index: key,
    })
  );
  const isRoomForDirectionArrow = useMediaQuery({
    minWidth: (currentFloor?.size?.x ?? 0) + 70,
  });

  useEffect(() => {
    if (containerRef.current && currentFloor?.size) {
      setCanvasReady(true);
    }
  }, [currentFloor?.size]);

  useEffect(() => {
    if (
      !canvasReady ||
      !selectedReservationId ||
      !containerRef.current ||
      !currentFloor?.size?.y
    ) {
      return;
    }

    const rawCoordinates = getSelectedSeatCoordinates(
      currentFloor,
      selectedReservationId
    );

    if (!rawCoordinates) {
      return;
    }

    const canvasHeight = currentFloor.size.y;
    const container = containerRef.current;

    const frameId = requestAnimationFrame(() => {
      const scrollParent = getScrollParent(container);

      if (!scrollParent) {
        return;
      }

      const { x, y } = transformCoordinates(
        rawCoordinates.x,
        rawCoordinates.y,
        canvasHeight
      );
      const parentRect = scrollParent.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();
      const seatOffsetTop =
        containerRect.top - parentRect.top + scrollParent.scrollTop + y;
      const seatOffsetLeft =
        containerRect.left - parentRect.left + scrollParent.scrollLeft + x;
      const targetScrollTop = Math.max(
        0,
        Math.min(
          seatOffsetTop - scrollParent.clientHeight / 2,
          scrollParent.scrollHeight - scrollParent.clientHeight
        )
      );
      const targetScrollLeft = Math.max(
        0,
        Math.min(
          seatOffsetLeft - scrollParent.clientWidth / 2,
          scrollParent.scrollWidth - scrollParent.clientWidth
        )
      );

      scrollParent.scrollTo({
        top: targetScrollTop,
        left: targetScrollLeft,
        behavior: 'smooth',
      });
    });

    return () => cancelAnimationFrame(frameId);
  }, [
    canvasReady,
    selectedReservationId,
    selectedFloorIndex,
    selectedCarriageIndex,
    currentFloor,
  ]);

  if (!currentFloor?.size?.x || !currentFloor?.size?.y || !floorPlanIcons) {
    return null;
  }

  return (
    <div className="relative mx-auto w-fit">
      <div
        ref={containerRef}
        className="cursor-pointer border border-neutral-light"
        style={{
          width: `${currentFloor.size.x}px`,
          height: `${currentFloor.size.y}px`,
        }}
      >
        {canvasReady && containerRef.current && (
          <VehicleLayoutCanvas
            container={containerRef.current}
            currentFloor={currentFloor}
            floorPlanIcons={floorPlanIcons}
            selectedReservationId={selectedReservationId}
            onSeatSelect={handleSeatSelect}
            onPassengerSeatClick={handlePassengerSeatClick}
            allPassengers={allPassengers}
          />
        )}
      </div>
      {isRoomForDirectionArrow && (
        <div className="absolute -right-16 top-10 flex -rotate-90 flex-col items-center">
          <Icons.arrow />
          <Typography variant="body1" className="rotate-180">
            <TransText i18nKey="front" />
          </Typography>
        </div>
      )}
    </div>
  );
};
